import { proxyImageSrcSet, proxyImageUrl } from "@/lib/image-url";
import type {
  PublicChannel,
  PublicProfile,
} from "@/lib/queries/public-profile";
import {
  getPlatformIcon,
  getPlatformLabel,
} from "@/components/channel/platform-utils";
import type { Platform } from "@/lib/queries/channel";
import { MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { InitialsAvatar } from "./initials-avatar";

// The portfolio's header (EPD-12163, EPD-12191). It must be rendered inside an
// ancestor named `@container/portfolio` (the portfolio renderer); anywhere else
// it stays in the stacked, small-photo layout.
interface PublicProfileHeaderProps {
  profile: PublicProfile;
  channels: PublicChannel[];
}

// Photo size (EPD-12294): 92px phone / 136px wide. The large photo arrives with
// the side-by-side layout at 40rem, not with the type scale at 30rem: between
// the two, a 136px photo plus the 40px gap would leave the name and bio about
// 220px in a 30rem container.
const AVATAR_SIZE = "h-23 w-23 @[40rem]/portfolio:h-34 @[40rem]/portfolio:w-34";

// Resized avatar variants (EPD-12468): uploads are often 1600px+ originals
// shown at 92/136px. Rungs of the worker's width ladder covering 1x–3x of the
// two sizes; `sizes` approximates the container breakpoint with the viewport
// (on the public page the portfolio container spans the viewport).
const AVATAR_WIDTHS = [128, 256, 512] as const;
const AVATAR_SIZES = "(min-width: 40rem) 136px, 92px";

function isSafeExternalUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

// Deduplicate channels by platform, keeping the first occurrence per platform
function uniqueByPlatform(channels: PublicChannel[]): PublicChannel[] {
  const seen = new Set<string>();
  return channels.filter((ch) => {
    if (seen.has(ch.platform)) return false;
    seen.add(ch.platform);
    return true;
  });
}

export function PublicProfileHeader({
  profile,
  channels,
}: PublicProfileHeaderProps) {
  const uniqueChannels = uniqueByPlatform(channels);

  // Avatar — themed initials placeholder when no photo (EPD-10138)
  const avatar = (
    <div className="shrink-0" data-slot="header-avatar">
      {profile.avatarUrl ? (
        // Eager (no `loading`) and high priority: the photo is the likely
        // largest paint on a phone (EPD-12468).
        <img
          src={proxyImageUrl(profile.avatarUrl, 256)}
          srcSet={proxyImageSrcSet(profile.avatarUrl, AVATAR_WIDTHS)}
          sizes={AVATAR_SIZES}
          fetchPriority="high"
          alt={profile.name}
          className={cn(
            "shrink-0 rounded-full bg-muted object-cover",
            AVATAR_SIZE,
          )}
        />
      ) : (
        // Accent fill (EPD-12191): the default surface fill matches the
        // default portfolio header color, which made the placeholder invisible.
        <InitialsAvatar
          name={profile.name}
          tone="accent"
          className={cn(AVATAR_SIZE, "text-3xl @[40rem]/portfolio:text-4xl")}
        />
      )}
    </div>
  );

  // min-w-0 lets long words wrap inside the flex item instead of pushing the
  // column past the header width.
  const details = (
    <div
      // Full width when stacked (so a long unbroken word wraps instead of
      // sizing the column past the screen), and a narrower column beside the
      // photo so the bio never crowds it (EPD-12294).
      className="w-full min-w-0 flex-1 space-y-3 @[30rem]/portfolio:max-w-xl"
      data-slot="header-details"
    >
      {/* Name */}
      {/* wrap-break-word throughout: a long unbroken name, place or URL in
          the bio wraps instead of forcing horizontal scroll on a phone. */}
      <h1 className="text-2xl font-bold wrap-break-word">{profile.name}</h1>

      {/* Location */}
      {profile.location && (
        <div className="flex items-center gap-1 text-muted-foreground text-sm">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 wrap-break-word">{profile.location}</span>
        </div>
      )}

      {/* Bio */}
      {profile.description && (
        <p className="text-muted-foreground wrap-break-word">
          {profile.description}
        </p>
      )}

      {/* Platform icons — no hover state or tooltip (EPD-12032); the
          aria-label carries the platform name for assistive tech. Small and
          filled with the foreground color (the theme's body text, near-black
          by default) rather than muted grey (EPD-12163). */}
      {uniqueChannels.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {uniqueChannels.map((channel) => {
            const Icon = getPlatformIcon(channel.platform as Platform);
            const label = getPlatformLabel(channel.platform as Platform);

            const icon = (
              <Icon
                className="h-4 w-4 text-foreground"
                data-slot="header-social-icon"
              />
            );

            const hasSafeUrl =
              channel.profileUrl && isSafeExternalUrl(channel.profileUrl);
            return hasSafeUrl ? (
              <a
                key={channel.id}
                href={channel.profileUrl ?? ""}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
              >
                {icon}
              </a>
            ) : (
              <span key={channel.id} role="img" aria-label={label}>
                {icon}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );

  // Text first, larger photo on the right, both top-aligned (EPD-12294). The
  // portfolio renderer's own container decides the layout; the row needs more
  // room than its 30rem type-scale step (photo + gap + a readable text column),
  // so it switches at 40rem. Below that the column stacks with the photo on top
  // (flex-col-reverse keeps the text first in reading order).
  return (
    <div className="flex flex-col-reverse items-start gap-5 @[40rem]/portfolio:flex-row @[40rem]/portfolio:justify-between @[40rem]/portfolio:gap-10">
      {details}
      {avatar}
    </div>
  );
}
